import { useRef, useState } from 'react';

import EmptyWorkspace from './empty-workspace';
import TaskCard from './task-card';
import TaskConnection from './task-connection';

import type { Task } from '@/types/experience';

interface WorkspaceContentProps {
  tasks: Task[];
  isReorderMode?: boolean;
  onCreate: () => void;
  onEditTask: (task: Task) => void;
  onDeleteTask: (taskId: string) => void;
  onDuplicateTask: (taskId: string) => void;
  onInsertAt: (level: number) => void;
  onReorder: (fromId: string, toLevel: number) => void;
}

function groupByLevel(tasks: Task[]) {
  return Array.from(
    tasks.reduce((map, t) => {
      const bucket = map.get(t.level) ?? [];
      bucket.push(t);
      map.set(t.level, bucket);
      return map;
    }, new Map<number, Task[]>()),
  ).sort(([a], [b]) => a - b);
}

export default function WorkspaceContent({
  tasks,
  isReorderMode = false,
  onCreate,
  onEditTask,
  onDeleteTask,
  onDuplicateTask,
  onInsertAt,
  onReorder,
}: WorkspaceContentProps) {
  const draggingIdRef = useRef<string | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dropKey, setDropKey] = useState<string | null>(null);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);

  if (tasks.length === 0) {
    return <EmptyWorkspace onCreate={onCreate} />;
  }

  const levels = groupByLevel(tasks);
  const selectedLevel = tasks.find((t) => t.id === selectedTaskId)?.level ?? null;

  // ── Reorder helpers ──────────────────────────────────────────────────────

  function resetDrag() {
    draggingIdRef.current = null;
    setDraggingId(null);
    setDropKey(null);
  }

  function handleDrop(targetLevel: number) {
    const id = draggingIdRef.current;
    if (!id) return;
    const from = tasks.find((t) => t.id === id);
    if (!from || from.level === targetLevel) { resetDrag(); return; }
    onReorder(id, targetLevel);
    resetDrag();
  }

  function rowDragOver(e: React.DragEvent, level: number) {
    e.preventDefault();
    const sourceLevel = tasks.find((t) => t.id === draggingIdRef.current)?.level;
    if (sourceLevel === level) return;
    setDropKey(`row-${level}`);
  }

  function rowDragLeave(e: React.DragEvent) {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) setDropKey(null);
  }

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div
      className='flex flex-col p-3'
      onDragEnd={resetDrag}
      onClick={() => setSelectedTaskId(null)}
    >
      {levels.map(([level, levelTasks], idx, arr) => {
        const isDropTarget = isReorderMode && dropKey === `row-${level}`;
        const isLast = idx === arr.length - 1;
        const nextLevel = level + 1;
        const prevLevelValue = idx > 0 ? arr[idx - 1]![0] : null;

        // + appears in the connection below level[idx-1] when selected task is there
        const showPlusAbove = prevLevelValue !== null && selectedLevel === prevLevelValue;
        // + appears after the last level when selected task is the last level
        const showPlusTrailing = isLast && selectedLevel === level;

        return (
          <div key={level}>
            {idx > 0 && (
              <TaskConnection
                showPlus={showPlusAbove}
                onInsert={() => onInsertAt(level)}
                isReorderMode={isReorderMode}
              />
            )}
            <div
              className={`flex justify-center gap-3 ${levelTasks.length > 1 ? 'overflow-x-auto' : ''}`}
              onDragOver={isReorderMode ? (e) => rowDragOver(e, level) : undefined}
              onDragLeave={isReorderMode ? rowDragLeave : undefined}
              onDrop={isReorderMode ? () => handleDrop(level) : undefined}
            >
              {levelTasks
                .sort((a, b) => a.order - b.order)
                .map((task) => {
                  if (isReorderMode) {
                    const prevLevel = idx > 0 ? arr[idx - 1]![0] : null;
                    const nextLevelVal = idx < arr.length - 1 ? arr[idx + 1]![0] : null;
                    return (
                      <div
                        key={task.id}
                        draggable
                        onClick={(e) => { e.stopPropagation(); setSelectedTaskId((prev) => prev === task.id ? null : task.id); }}
                        onDragStart={(e) => {
                          e.dataTransfer.effectAllowed = 'move';
                          draggingIdRef.current = task.id;
                          const rect = e.currentTarget.getBoundingClientRect();
                          e.dataTransfer.setDragImage(e.currentTarget, e.clientX - rect.left, e.clientY - rect.top);
                          requestAnimationFrame(() => setDraggingId(task.id));
                        }}
                        className={`min-w-[246px] rounded-lg transition-all ${
                          isDropTarget ? 'bg-ws-primary/10 outline outline-2 outline-ws-primary outline-offset-1' : ''
                        } ${draggingId === task.id ? 'opacity-40' : 'opacity-100'}`}
                      >
                        <TaskCard
                          task={task}
                          isReorderMode
                          isSelected={selectedTaskId === task.id}
                          onMoveUp={prevLevel !== null ? () => onReorder(task.id, prevLevel) : undefined}
                          onMoveDown={nextLevelVal !== null ? () => onReorder(task.id, nextLevelVal) : undefined}
                          onEdit={() => {}}
                          onDelete={() => {}}
                          onDuplicate={() => {}}
                        />
                      </div>
                    );
                  }
                  return (
                    <div
                      key={task.id}
                      className='min-w-[246px]'
                      onClick={(e) => { e.stopPropagation(); setSelectedTaskId((prev) => prev === task.id ? null : task.id); }}
                    >
                      <TaskCard
                        task={task}
                        isSelected={selectedTaskId === task.id}
                        onEdit={onEditTask}
                        onDelete={onDeleteTask}
                        onDuplicate={onDuplicateTask}
                      />
                    </div>
                  );
                })}
            </div>
            <TaskConnection
              showPlus={showPlusTrailing}
              onInsert={() => onInsertAt(nextLevel)}
              isReorderMode={isReorderMode}
              isTrailing
            />
          </div>
        );
      })}
    </div>
  );
}
